'use client'

import { z } from 'zod'

import {
  importAnalysisSchema,
  importConfirmedSchema,
} from '@/features/studio-renew/schemas/import-result.schema'
import {
  serverImportAnalyzeSchema,
  serverImportConfirmSchema,
} from '@/features/studio-renew/schemas/server-import.schema'
import {
  analyzeServerImport,
  confirmServerImport,
  resumeServerImport,
} from '@/features/studio-renew/services/server-import.service'
import { studioSession } from '@/features/studio-renew/services/studio-session'
import { ApiError } from '@/features/veluvit/services/api'

import type { ImportTarget } from '@/features/studio-renew/services/studio-import.service'
import type {
  ServerImportAnalyze,
  ServerImportConfirm,
} from '@/features/studio-renew/schemas/server-import.schema'
import type { OperationOptions } from '@/features/veluvit/services/api'

export type ImportRun = {
  phase: 'ai' | 'compare' | 'confirm'
  text: string
  destination: ImportTarget
  payload: Record<string, unknown>
  operationId?: string
  status?: string
  done: boolean
  rejected: boolean
}
export type ImportRunScope = { owner: string; target: ImportTarget }
const phases = ['confirm', 'compare', 'ai'] as const
const runs = new Map<string, ImportRun>()
function key(scope: ImportRunScope, phase: ImportRun['phase']) {
  return `veluvit:import:${encodeURIComponent(scope.owner)}:${scope.target.kind}:${encodeURIComponent(scope.target.id ?? 'new')}:${phase}`
}
export function readImportRun(
  scope: ImportRunScope,
  phase?: ImportRun['phase']
): ImportRun | null {
  for (const candidate of phase ? [phase] : phases) {
    const run = runs.get(key(scope, candidate))
    if (run) return run
  }
  return null
}
function write(scope: ImportRunScope, run: ImportRun) {
  runs.set(key(scope, run.phase), run)
}
export function clearImportRuns(scope: ImportRunScope) {
  for (const phase of phases) runs.delete(key(scope, phase))
}

function prepare(
  scope: ImportRunScope,
  phase: ImportRun['phase'],
  text: string,
  destination: ImportTarget,
  input: Record<string, unknown>
) {
  const previous = readImportRun(scope, phase)
  if (
    previous &&
    !previous.rejected &&
    !['failed', 'cancelled', 'invalidated'].includes(previous.status ?? '')
  ) {
    const { requestId: _id, ...previousBody } = previous.payload
    if (JSON.stringify(previousBody) === JSON.stringify(input)) {
      const updated = { ...previous, destination }
      write(scope, updated)
      return updated
    }
    if (!previous.done)
      throw new Error(
        '이전 이식 작업의 결과를 먼저 확인해 주세요. 원문은 보관되어 있어요.'
      )
  }
  const run: ImportRun = {
    phase,
    text,
    destination,
    payload: { ...input, requestId: crypto.randomUUID() },
    done: false,
    rejected: false,
  }
  write(scope, run)
  return run
}

async function execute(
  scope: ImportRunScope,
  snapshot: ImportRun,
  options: OperationOptions = {}
) {
  const session = studioSession()
  if (session.owner !== scope.owner)
    throw new Error('이식 작업의 작가 계정이 달라요.')
  const run = { ...snapshot }
  const payload =
    run.phase === 'confirm'
      ? serverImportConfirmSchema.parse(run.payload)
      : serverImportAnalyzeSchema.parse(run.payload)
  const watch: OperationOptions = {
    ...options,
    onOperation(operation) {
      session.assertCurrent()
      run.operationId = operation.id
      run.status = operation.status
      write(scope, run)
      options.onOperation?.(operation)
    },
  }
  try {
    let value: unknown
    if (run.operationId)
      value = await resumeServerImport(
        run.operationId,
        payload.requestId,
        watch
      )
    else if (run.phase === 'confirm')
      value = await confirmServerImport(
        serverImportConfirmSchema.parse(payload),
        watch
      )
    else
      value = await analyzeServerImport(
        serverImportAnalyzeSchema.parse(payload),
        watch
      )
    session.assertCurrent()
    const result =
      run.phase === 'confirm'
        ? importConfirmedSchema.parse(value)
        : importAnalysisSchema.parse(value)
    if (result.applied && result.analysisId !== run.payload.analysisId)
      throw new Error('확정 결과가 선택한 분석과 일치하지 않아요.')
    if (!run.operationId)
      throw new Error('서버의 이식 작업 ID를 확인하지 못했어요.')
    run.done = true
    write(scope, run)
    return { operationId: run.operationId, result }
  } catch (error) {
    session.assertCurrent()
    if (error instanceof z.ZodError)
      throw new ApiError(
        '이식 결과의 형식을 확인하지 못했어요. 기존 작업을 다시 확인해 주세요.',
        502,
        'INVALID_IMPORT_RESULT'
      )
    if (
      error instanceof ApiError &&
      !error.unknownOutcome &&
      error.status >= 400 &&
      error.status < 500 &&
      !run.operationId
    ) {
      run.rejected = true
      write(scope, run)
    }
    throw error
  }
}

export async function startImportAnalysis(
  scope: ImportRunScope,
  phase: 'ai' | 'compare',
  input: Omit<ServerImportAnalyze, 'requestId'>,
  destination: ImportTarget,
  options: OperationOptions = {}
) {
  const snapshot = prepare(scope, phase, input.text, destination, input)
  const outcome = await execute(scope, snapshot, options)
  return {
    analysisId: outcome.operationId,
    result: importAnalysisSchema.parse(outcome.result),
  }
}
export async function startImportConfirm(
  scope: ImportRunScope,
  input: Omit<ServerImportConfirm, 'requestId'>,
  text: string,
  destination: ImportTarget,
  options: OperationOptions = {}
) {
  const snapshot = prepare(scope, 'confirm', text, destination, input)
  return importConfirmedSchema.parse(
    (await execute(scope, snapshot, options)).result
  )
}
export async function restoreImportRun(
  scope: ImportRunScope,
  snapshot: ImportRun,
  options: OperationOptions = {}
) {
  return execute(scope, snapshot, options)
}
